import { ARI_INBOX_THREADS, ARI_LABELS } from "@/mockData/ariInbox";
import { resolveAriBrandId } from "@/lib/ariBrands";
import type { AriBrandId } from "@/lib/ariBrands";
import { PATH_ROOT } from "@/routes/paths";
import type { AriDealLabelId, Deal, PipelineStage } from "@/types/deal.types";

type AriPipelineCol = "lead" | "in_progress" | "won" | "lost" | "closed";

const COL_TO_STAGE: Record<AriPipelineCol, PipelineStage> = {
  lead: "lead",
  in_progress: "in_progress",
  won: "won",
  lost: "lost",
  closed: "closed",
};

const LABEL_TO_DEAL_TYPE: Record<AriDealLabelId, Deal["dealType"]> = {
  "music-deal": "music",
  publishing: "music",
  touring: "music",
  "brand-deal": "partnership",
  endorsement: "partnership",
  licensing: "partnership",
  "sports-deal": "partnership",
  client: "partnership",
  finance: "vc",
};

interface AriPipelineSeed {
  id: string;
  title: string;
  person: string;
  company: string;
  amount: number;
  closeDate: string;
  label: AriDealLabelId;
  col: AriPipelineCol;
  task: string;
  brandId?: AriBrandId;
}

const ARI_PIPELINE_SEED: AriPipelineSeed[] = [
  {
    id: "ari-pipeline-1",
    title: "Jay-Z Catalog Publishing",
    person: "Marcus Webb",
    company: "Roc Nation",
    amount: 85_000_000,
    closeDate: "Jun 30, 2026",
    label: "music-deal",
    col: "in_progress",
    task: "Send redline by Thursday",
  },
  {
    id: "ari-pipeline-2",
    title: "Savage x Fenty x Nike",
    person: "Dominique Azari",
    company: "Fenty Corp",
    amount: 20_000_000,
    closeDate: "May 15, 2026",
    label: "brand-deal",
    col: "in_progress",
    task: "Push back on exclusivity clause",
    brandId: "nike",
  },
  {
    id: "ari-pipeline-3",
    title: "LeBron Nike Royalty Amendment",
    person: "Rich Paul",
    company: "Klutch Sports",
    amount: 50_000_000,
    closeDate: "May 9, 2026",
    label: "endorsement",
    col: "in_progress",
    task: "Sign-off on equity conversion",
    brandId: "nike",
  },
  {
    id: "ari-pipeline-4",
    title: "Kobe Nike Reacquisition",
    person: "Vanessa Bryant",
    company: "Mamba Sports",
    amount: 0,
    closeDate: "",
    label: "licensing",
    col: "lead",
    task: "",
    brandId: "nike",
  },
  {
    id: "ari-pipeline-5",
    title: "Renaissance II Tour Rights",
    person: "Parkwood Legal",
    company: "Parkwood Entertainment",
    amount: 0,
    closeDate: "",
    label: "touring",
    col: "won",
    task: "",
  },
  {
    id: "ari-pipeline-6",
    title: "Travis x McDonald's Phase 2",
    person: "Cactus Jack Mgmt",
    company: "Cactus Jack Records",
    amount: 20_000_000,
    closeDate: "May 30, 2026",
    label: "brand-deal",
    col: "lead",
    task: "Review IP ownership clause",
    brandId: "mcdonalds",
  },
  {
    id: "ari-pipeline-7",
    title: "Serena Wilson Legacy Deal",
    person: "WTA Partnerships",
    company: "WTA",
    amount: 20_000_000,
    closeDate: "Jun 1, 2026",
    label: "sports-deal",
    col: "closed",
    task: "",
  },
  {
    id: "ari-pipeline-8",
    title: "Atlas Ventures Series B",
    person: "Michael Torres",
    company: "Atlas Ventures",
    amount: 0,
    closeDate: "May 9, 2026",
    label: "finance",
    col: "in_progress",
    task: "Prepare DD materials",
  },
  {
    id: "ari-pipeline-9",
    title: "Nexus Partners MSA Renewal",
    person: "David Kim",
    company: "Nexus Partners",
    amount: 0,
    closeDate: "Jun 30, 2026",
    label: "client",
    col: "lead",
    task: "Schedule renewal call",
  },
  {
    id: "ari-pipeline-10",
    title: "Lighthouse Advisory Partnership",
    person: "Alex Rivera",
    company: "Lighthouse Advisory",
    amount: 0,
    closeDate: "May 31, 2026",
    label: "client",
    col: "lead",
    task: "Review MOU terms",
  },
];

function seedToDeal(seed: AriPipelineSeed): Deal {
  const labelName = ARI_LABELS[seed.label]?.name ?? seed.label;
  return {
    id: seed.id,
    name: seed.title,
    type: labelName,
    dealType: LABEL_TO_DEAL_TYPE[seed.label],
    stage: COL_TO_STAGE[seed.col],
    value: seed.amount,
    date: seed.closeDate || "Apr 2026",
    progress: 0,
    documentsCount: 0,
    collaborators: [],
    lastActivity: "Recently",
    dueDate: seed.closeDate || null,
    description: seed.task || `${seed.person} · ${seed.company}`,
    contactName: seed.person,
    contactCompany: seed.company,
    nextTask: seed.task,
    dealLabelId: seed.label,
    brandId: seed.brandId ?? resolveAriBrandId(seed.title, seed.company) ?? undefined,
  };
}

export const ARI_PIPELINE_DEALS: Deal[] = ARI_PIPELINE_SEED.map(seedToDeal);

/** Maps pipeline card id → inbox thread opened as the Ari demo deal room. */
const ARI_PIPELINE_DEAL_INBOX_THREAD: Record<string, string> = {
  "ari-pipeline-1": "ari-thread-1",
  "ari-pipeline-2": "ari-thread-2",
  "ari-pipeline-3": "ari-thread-3",
  "ari-pipeline-4": "ari-thread-4",
  "ari-pipeline-5": "ari-thread-5",
  "ari-pipeline-6": "ari-thread-6",
  "ari-pipeline-7": "ari-thread-7",
};

export function resolveAriDealRoomHref(dealId: string): string {
  const suggestedMatch = dealId.match(/^ari-suggested-(\d+)$/);
  if (suggestedMatch) {
    return `${PATH_ROOT.demo.inbox}?dealRoom=ari-thread-${suggestedMatch[1]}`;
  }

  const threadId = ARI_PIPELINE_DEAL_INBOX_THREAD[dealId];
  if (threadId) {
    return `${PATH_ROOT.demo.inbox}?dealRoom=${threadId}`;
  }

  return `${PATH_ROOT.demo.deal}/${dealId}`;
}

/** Maps Ari pipeline deal → demo deal page shape */
export function mapAriDealToDemoDeal(deal: Deal) {
  const status =
    deal.stage === "complete" || deal.stage === "won" || deal.stage === "closed"
      ? ("Completed" as const)
      : deal.stage === "signing" || deal.stage === "delivery"
        ? ("Pending" as const)
        : deal.stage === "in_progress"
          ? ("In Progress" as const)
          : deal.stage === "lost"
            ? ("Draft" as const)
            : ("Draft" as const);

  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(deal.value);

  return {
    id: deal.id,
    name: deal.name,
    type: deal.type,
    status,
    value: deal.value > 0 ? formattedValue : "—",
    date: deal.date,
    updatedAt: deal.lastActivity,
    documentCount: deal.documentsCount,
    coreDocument: deal.contactName ? `${deal.contactName} — ${deal.type}` : `${deal.type} Agreement`,
    coreContractId: undefined as string | undefined,
  };
}

/** Suggested from inbox — same pattern as ari/email-client SUGGESTED_FROM_EMAILS */
export const ARI_SUGGESTED_DEALS: Deal[] = ARI_INBOX_THREADS.map((thread, index) => {
  const label = (thread.dealTags[0] ?? "client") as AriDealLabelId;
  const labelName = ARI_LABELS[label]?.name ?? label;
  const company =
    thread.from.email.split("@")[1]?.replace(/\..+$/, "").replace(/-/g, " ") ?? "Unknown";

  return {
    id: `ari-suggested-${index + 1}`,
    name: thread.subject.split("—")[0].split("-")[0].trim(),
    type: labelName,
    dealType: LABEL_TO_DEAL_TYPE[label] ?? "partnership",
    stage: "lead",
    value: 0,
    date: thread.date,
    progress: 0,
    documentsCount: 0,
    collaborators: [],
    lastActivity: thread.date,
    dueDate: null,
    description: thread.aiSummary,
    contactName: thread.from.name,
    contactCompany: company.charAt(0).toUpperCase() + company.slice(1),
    nextTask: "",
    dealLabelId: label,
    brandId: resolveAriBrandId(thread.subject, company) ?? undefined,
  };
});
